import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import './OrderMatrixPage.css';

// Order Matrix — product x colour grid backed by SKUs (see myapp/order_matrix.py).
// The layout comes from the live catalog; a saved matrix is just {sku: qty}.

const KINDS = [
  { value: 'order', label: 'Order' },
  { value: 'count', label: 'Count' },
  { value: 'inventory', label: 'Inventory' },
  { value: 'other', label: 'Other' },
];

const SWATCH = {
  'No Color': 'transparent', White: '#f8fafc', Blue: '#3b82f6', Black: '#0b0b0b', Red: '#ef4444',
  Green: '#22c55e', Grey: '#9ca3af', Purple: '#a855f7', Pink: '#f472b6', Teal: '#14b8a6',
  Orange: '#f97316', Yellow: '#facc15',
};

const COLLAPSE_KEY = 'orderMatrix.collapsed';

function fmt(n) {
  if (!n) return '';
  return Number.isInteger(n) ? String(n) : String(+n.toFixed(2));
}

function parseNum(s) {
  const t = String(s ?? '').trim().replace(/[,\s]/g, '');
  if (t === '' || t === '-' || t === '—') return 0;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

function timeAgo(ts) {
  if (!ts) return '';
  const s = Date.now() / 1000 - ts;
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return new Date(ts * 1000).toLocaleDateString();
}

function errText(err, what) {
  const status = err.response?.status;
  const detail = err.response?.data?.error;
  if (status === 401) return `${what} — you may have been signed out. Refresh the page.`;
  return `${what}${status ? ` (${status})` : ''}${detail ? `: ${detail}` : ''}`;
}

// Same shape as order_matrix.grid_rows() on the server, so a copied grid
// pastes back through /api/order-matrix/parse.
function gridTsv(layout, q) {
  const cols = layout.columns;
  const lines = [['Product', 'Code', ...cols, 'Total']];
  const colTot = Object.fromEntries(cols.map(c => [c, 0]));
  for (const row of layout.rows) {
    if (row.kind === 'header') { lines.push([row.label, '', ...cols.map(() => ''), '']); continue; }
    let tot = 0;
    const cells = cols.map(c => {
      const sku = row.cells[c];
      if (!sku) return '';
      const v = q[sku] || 0;
      tot += v; colTot[c] += v;
      return fmt(v);
    });
    lines.push([row.label, row.code, ...cells, fmt(tot)]);
  }
  lines.push(['Total', '', ...cols.map(c => fmt(colTot[c])), fmt(Object.values(colTot).reduce((a, b) => a + b, 0))]);
  return lines.map(l => l.join('\t')).join('\n');
}

function skuListTsv(layout, q) {
  const lines = [['SKU', 'Product', 'Color', 'Qty']];
  const seen = new Set();
  for (const row of layout.rows) {
    for (const c of layout.columns) {
      const sku = row.cells[c];
      if (!sku) continue;
      seen.add(sku);
      if (q[sku]) lines.push([sku, row.label, row.kind === 'item' ? '' : c, fmt(q[sku])]);
    }
  }
  Object.keys(q).filter(s => !seen.has(s) && q[s]).sort()
    .forEach(s => lines.push([s, '(not in current matrix layout)', '', fmt(q[s])]));
  return lines.map(l => l.join('\t')).join('\n');
}

// Short display name for a stored email; "you" for the viewer.
function who(email, me) {
  if (!email) return 'unknown';
  if (me && email.toLowerCase() === me.toLowerCase()) return 'you';
  return email.split('@')[0];
}

const OWNER_FILTER_KEY = 'orderMatrix.ownerFilter';

async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const ta = document.createElement('textarea');
    ta.value = text;
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  }
}

export default function OrderMatrixPage({ user }) {
  const navigate = useNavigate();
  const { matrixId } = useParams();
  const me = user?.email || null;
  const [ownerFilter, setOwnerFilter] = useState(() => {
    try { return localStorage.getItem(OWNER_FILTER_KEY) === 'mine' ? 'mine' : 'all'; } catch { return 'all'; }
  });
  function changeOwnerFilter(v) {
    setOwnerFilter(v);
    try { localStorage.setItem(OWNER_FILTER_KEY, v); } catch { /* storage unavailable */ }
  }

  const [layout, setLayout] = useState(null);
  const [list, setList] = useState([]);
  const [matrix, setMatrix] = useState(null);
  const [qty, setQty] = useState({});
  const [error, setError] = useState(null);
  const [flash, setFlash] = useState(null);
  const [saveState, setSaveState] = useState('saved'); // saved | pending | saving | error
  const [compareId, setCompareId] = useState('');
  const [compareQty, setCompareQty] = useState(null);
  const [collapsed, setCollapsed] = useState(() => {
    try { return JSON.parse(localStorage.getItem(COLLAPSE_KEY) || '{}'); } catch { return {}; }
  });
  const [showNew, setShowNew] = useState(false);
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteInitial, setPasteInitial] = useState('');
  const [historyOpen, setHistoryOpen] = useState(false);
  const [editing, setEditing] = useState(null); // {sku, text}

  // Artist-mat style groups start collapsed (so the matrix fits one screen);
  // a header click is remembered per browser.
  const isCollapsed = useCallback(key => (key in collapsed ? collapsed[key] : true), [collapsed]);
  const headerKeys = useMemo(() => (layout ? layout.rows.filter(r => r.kind === 'header').map(r => r.key) : []), [layout]);
  const anyExpanded = headerKeys.some(k => !isCollapsed(k));
  function setAllCollapsed(v) { setCollapsed(Object.fromEntries(headerKeys.map(k => [k, v]))); }

  const saveTimer = useRef(null);
  const pendingQty = useRef(null);
  const inputs = useRef({});

  // ── loading ──
  const loadLayout = useCallback((refresh) => {
    return axios.get(`/api/order-matrix/layout${refresh ? '?refresh=1' : ''}`)
      .then(r => setLayout(r.data))
      .catch(err => setError(errText(err, 'Failed to load the product layout')));
  }, []);

  const loadList = useCallback(() => {
    return axios.get('/api/order-matrix/matrices')
      .then(r => setList(r.data || []))
      .catch(err => setError(errText(err, 'Failed to load saved matrices')));
  }, []);

  useEffect(() => { loadLayout(false); loadList(); }, [loadLayout, loadList]);

  useEffect(() => {
    if (!matrixId) { setMatrix(null); setQty({}); return; }
    axios.get(`/api/order-matrix/matrices/${matrixId}`)
      .then(r => { setMatrix(r.data); setQty(r.data.quantities || {}); setSaveState('saved'); })
      .catch(err => setError(errText(err, 'Failed to load matrix')));
  }, [matrixId]);

  useEffect(() => {
    if (!compareId) { setCompareQty(null); return; }
    axios.get(`/api/order-matrix/matrices/${compareId}`)
      .then(r => setCompareQty(r.data.quantities || {}))
      .catch(err => setError(errText(err, 'Failed to load comparison matrix')));
  }, [compareId]);

  useEffect(() => { localStorage.setItem(COLLAPSE_KEY, JSON.stringify(collapsed)); }, [collapsed]);

  // ── saving ──
  const flushSave = useCallback((extra = {}) => {
    if (!matrix) return Promise.resolve();
    clearTimeout(saveTimer.current);
    const body = { ...extra };
    if (pendingQty.current) { body.quantities = pendingQty.current; pendingQty.current = null; }
    if (!Object.keys(body).length) return Promise.resolve();
    setSaveState('saving');
    return axios.put(`/api/order-matrix/matrices/${matrix.id}`, body)
      .then(r => {
        setMatrix(r.data);
        setSaveState(pendingQty.current ? 'pending' : 'saved');
        loadList();
        return r.data;
      })
      .catch(err => { setSaveState('error'); setError(errText(err, 'Save failed')); });
  }, [matrix, loadList]);

  const setQuantities = useCallback((next, { immediate = false, snapshot = false, note } = {}) => {
    setQty(next);
    pendingQty.current = next;
    setSaveState('pending');
    clearTimeout(saveTimer.current);
    if (immediate) flushSave(snapshot ? { snapshot: true, note } : {});
    else saveTimer.current = setTimeout(() => flushSave(), 700);
  }, [flushSave]);

  useEffect(() => {
    const warn = e => { if (pendingQty.current) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, []);

  function openMatrix(id) {
    if (pendingQty.current) flushSave();
    setCompareId('');
    navigate(`/order-matrix/${id}`);
  }

  function showFlash(msg) {
    setFlash(msg);
    setTimeout(() => setFlash(f => (f === msg ? null : f)), 2500);
  }

  // ── grid geometry ──
  const visibleRows = useMemo(() => {
    if (!layout) return [];
    const out = [];
    let hidden = null;
    for (const row of layout.rows) {
      if (row.kind === 'header') { hidden = isCollapsed(row.key) ? row.key : null; out.push(row); continue; }
      if (row.kind === 'family') hidden = null;
      if (hidden && row.kind === 'item') continue;
      out.push(row);
    }
    return out;
  }, [layout, isCollapsed]);

  const editRows = useMemo(() => visibleRows.filter(r => r.kind !== 'header'), [visibleRows]);

  // header key -> sum of its hidden items, shown on a collapsed header
  const groupSums = useMemo(() => {
    if (!layout) return {};
    const sums = {};
    let cur = null;
    for (const row of layout.rows) {
      if (row.kind === 'header') { cur = row.key; sums[cur] = 0; continue; }
      if (row.kind === 'family') cur = null;
      if (cur && row.kind === 'item') for (const s of Object.values(row.cells)) sums[cur] += qty[s] || 0;
    }
    return sums;
  }, [layout, qty]);

  const totals = useMemo(() => {
    if (!layout) return { cols: {}, grand: 0, skus: 0, value: 0 };
    const cols = Object.fromEntries(layout.columns.map(c => [c, 0]));
    let grand = 0, skus = 0, value = 0;
    for (const row of layout.rows) {
      for (const [c, sku] of Object.entries(row.cells)) {
        const v = qty[sku] || 0;
        if (!v) continue;
        cols[c] += v; grand += v; skus += 1;
        const price = parseFloat(layout.skus[sku]?.price);
        if (price) value += v * price;
      }
    }
    return { cols, grand, skus, value };
  }, [layout, qty]);

  const orphanSkus = useMemo(() => {
    if (!layout) return [];
    return Object.keys(qty).filter(s => qty[s] && !layout.skus[s]);
  }, [layout, qty]);

  function focusCell(r, c) {
    const el = inputs.current[`${r}:${c}`];
    if (el) { el.focus(); el.select(); return true; }
    return false;
  }

  function moveFrom(r, c, dr, dc) {
    const cols = layout.columns.length;
    let nr = r + dr, nc = c + dc;
    while (nr >= 0 && nr < editRows.length && nc >= 0 && nc < cols) {
      if (focusCell(nr, nc)) return;
      nr += dr; nc += dc;
    }
  }

  function onCellKey(e, r, c) {
    const el = e.target;
    if (e.key === 'Enter' || e.key === 'ArrowDown') { e.preventDefault(); moveFrom(r, c, e.shiftKey && e.key === 'Enter' ? -1 : 1, 0); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); moveFrom(r, c, -1, 0); }
    else if (e.key === 'ArrowRight' && el.selectionStart === el.value.length) { e.preventDefault(); moveFrom(r, c, 0, 1); }
    else if (e.key === 'ArrowLeft' && el.selectionEnd === 0) { e.preventDefault(); moveFrom(r, c, 0, -1); }
    else if (e.key === 'Escape') { setEditing(null); el.blur(); }
  }

  function commitCell(sku, text) {
    const n = parseNum(text);
    if (n === null) return;
    if ((qty[sku] || 0) === n) return;
    const next = { ...qty };
    if (n) next[sku] = n; else delete next[sku];
    setQuantities(next);
  }

  // Multi-cell paste into the grid: numbers fill right/down from this cell,
  // like a spreadsheet. A paste that includes labels/headers goes to the
  // paste dialog instead, where it's matched by row and colour name.
  function onCellPaste(e, r, c) {
    const text = e.clipboardData.getData('text/plain');
    if (!/[\t\n]/.test(text.trim())) return;
    e.preventDefault();
    const lines = text.replace(/\r/g, '').replace(/\n$/, '').split('\n').map(l => l.split('\t'));
    if (lines.some(l => l.some(v => parseNum(v) === null))) {
      setPasteInitial(text); setPasteOpen(true); return;
    }
    const next = { ...qty };
    let n = 0, skipped = 0;
    lines.forEach((line, i) => {
      const row = editRows[r + i];
      if (!row) { skipped += line.length; return; }
      line.forEach((v, j) => {
        const color = layout.columns[c + j];
        const sku = color && row.cells[color];
        const val = parseNum(v);
        if (!sku) { if (val) skipped += 1; return; }
        if (val) next[sku] = val; else delete next[sku];
        n += 1;
      });
    });
    setEditing(null);
    setQuantities(next, { immediate: true, snapshot: true, note: 'grid paste' });
    showFlash(`Pasted ${n} cell${n === 1 ? '' : 's'}${skipped ? ` — ${skipped} landed on no product and were ignored` : ''}`);
  }

  // ── actions ──
  async function doCopy(kind) {
    const text = kind === 'list' ? skuListTsv(layout, qty) : gridTsv(layout, qty);
    const ok = await copyText(text);
    showFlash(ok ? (kind === 'list' ? 'SKU list copied — paste into Sheets' : 'Matrix copied — paste into Sheets, edit, then copy it back and Paste') : 'Copy failed');
  }

  function download(view) {
    if (!matrix) return;
    flushSave().then(() => {
      window.location.href = `/api/order-matrix/matrices/${matrix.id}/export?view=${view}&format=csv&download=1`;
    });
  }

  function duplicate() {
    flushSave().then(() => axios.post('/api/order-matrix/matrices', {
      name: `${matrix.name} (copy)`, kind: matrix.kind, notes: matrix.notes, from: 'copy', copy_id: matrix.id,
    })).then(r => { loadList(); openMatrix(r.data.id); showFlash('Duplicated'); })
      .catch(err => setError(errText(err, 'Duplicate failed')));
  }

  function remove() {
    if (!window.confirm(`Delete "${matrix.name}"? This can't be undone.`)) return;
    clearTimeout(saveTimer.current); pendingQty.current = null;
    axios.delete(`/api/order-matrix/matrices/${matrix.id}`)
      .then(() => { loadList(); navigate('/order-matrix'); })
      .catch(err => setError(errText(err, 'Delete failed')));
  }

  function clearAll() {
    if (!window.confirm('Clear every quantity in this matrix? (It goes into History, so you can restore it.)')) return;
    setQuantities({}, { immediate: true, snapshot: true, note: 'before clear' });
  }

  function saveMeta(field, value) {
    if (!matrix || matrix[field] === value) return;
    setMatrix(m => ({ ...m, [field]: value }));
    flushSave({ [field]: value });
  }

  const compareOptions = list.filter(m => m.id !== matrix?.id);
  const shownList = ownerFilter === 'mine' && me
    ? list.filter(m => (m.created_by || '').toLowerCase() === me.toLowerCase())
    : list;
  const cols = layout?.columns || [];

  return (
    <div className="om-page">
      <aside className="om-side">
        <div className="om-side-head">
          <h2>Order Matrix</h2>
          <button className="om-btn om-btn-primary" onClick={() => setShowNew(v => !v)}>+ New</button>
        </div>
        {showNew && (
          <NewMatrixForm list={list} current={matrix}
            onCancel={() => setShowNew(false)}
            onCreated={m => { setShowNew(false); loadList(); openMatrix(m.id); }}
            onError={setError} />
        )}
        {me && (
          <div className="om-owner-filter">
            {[['all', 'Everyone'], ['mine', 'Mine']].map(([v, label]) => (
              <button key={v} className={ownerFilter === v ? 'active' : ''} onClick={() => changeOwnerFilter(v)}>{label}</button>
            ))}
          </div>
        )}
        <ul className="om-list">
          {shownList.map(m => (
            <li key={m.id} className={m.id === matrixId ? 'active' : ''} onClick={() => openMatrix(m.id)}>
              <div className="om-list-name">{m.name}</div>
              <div className="om-list-meta">
                <span className={`om-kind om-kind-${m.kind}`}>{m.kind}</span>
                {fmt(m.total_units) || 0} units · {timeAgo(m.updated_at)}
              </div>
              <div className="om-list-meta">
                by {who(m.created_by, me)}
                {m.updated_by && m.updated_by !== m.created_by && <> · edited by {who(m.updated_by, me)}</>}
              </div>
            </li>
          ))}
          {!shownList.length && <li className="om-empty">
            {list.length ? "You haven't created any matrices yet." : 'No saved matrices yet.'}
          </li>}
        </ul>
        {layout && (
          <div className="om-side-foot">
            Layout from {layout.source}, {timeAgo(layout.generated_at)}.{' '}
            <button className="om-link" onClick={() => loadLayout(true)}>Refresh</button>
          </div>
        )}
      </aside>

      <section className="om-main">
        {error && (
          <div className="om-error"><span>{error}</span><button onClick={() => setError(null)}>✕</button></div>
        )}
        {flash && <div className="om-flash">{flash}</div>}

        {!matrix && (
          <div className="om-intro">
            <h1>Order Matrix</h1>
            <p>Every product on one screen: product lines down the side, colours across the top, one cell per SKU.
              New products and colours appear on their own as they're added in Shopify.</p>
            <ul>
              <li><b>+ New</b> to start a blank order, copy an existing one, or load live stock from a location.</li>
              <li><b>Copy matrix</b> puts it on the clipboard — paste into Google Sheets, work on it, copy it back and hit <b>Paste</b>.</li>
              <li>Every saved matrix keeps its quantities by SKU, so <b>Copy SKU list</b> gives you the line-item version any time.</li>
            </ul>
          </div>
        )}

        {matrix && layout && (
          <>
            <div className="om-toolbar">
              <input className="om-title" defaultValue={matrix.name} key={`n-${matrix.id}`}
                onBlur={e => saveMeta('name', e.target.value.trim() || matrix.name)}
                onKeyDown={e => e.key === 'Enter' && e.target.blur()} />
              <select className="om-select" value={matrix.kind} onChange={e => saveMeta('kind', e.target.value)}>
                {KINDS.map(k => <option key={k.value} value={k.value}>{k.label}</option>)}
              </select>
              <span className={`om-save om-save-${saveState}`}>
                {{ saved: 'Saved', pending: 'Unsaved…', saving: 'Saving…', error: 'Save failed' }[saveState]}
              </span>
              <span className="om-authors">
                Created by {who(matrix.created_by, me)}
                {matrix.updated_by && <> · last edited by {who(matrix.updated_by, me)} {timeAgo(matrix.updated_at)}</>}
              </span>
              <div className="om-toolbar-spacer" />
              <button className="om-btn om-btn-primary" onClick={() => doCopy('grid')} title="Copy the matrix as cells for Google Sheets / Excel">Copy matrix</button>
              <button className="om-btn" onClick={() => { setPasteInitial(''); setPasteOpen(true); }} title="Paste a matrix or SKU list back in">Paste</button>
              <button className="om-btn" onClick={() => doCopy('list')} title="Copy non-zero SKUs as SKU / Product / Color / Qty lines">Copy SKU list</button>
              <Menu label="More ▾">
                <button onClick={() => download('grid')}>Download matrix CSV</button>
                <button onClick={() => download('list')}>Download SKU list CSV</button>
                <button onClick={duplicate}>Duplicate</button>
                <button onClick={() => setHistoryOpen(true)}>History ({matrix.history?.length || 0})</button>
                <button onClick={clearAll}>Clear all quantities</button>
                <button className="danger" onClick={remove}>Delete matrix</button>
              </Menu>
            </div>

            <div className="om-subbar">
              <input className="om-notes" defaultValue={matrix.notes} key={`no-${matrix.id}`} placeholder="Notes (customer, show, PO#…)"
                onBlur={e => saveMeta('notes', e.target.value)} onKeyDown={e => e.key === 'Enter' && e.target.blur()} />
              <label className="om-compare">
                Compare to
                <select className="om-select" value={compareId} onChange={e => setCompareId(e.target.value)}>
                  <option value="">—</option>
                  {compareOptions.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
              </label>
              {compareQty && (
                <span className="om-legend">
                  <i className="cmp-new" /> new <i className="cmp-up" /> increase <i className="cmp-down" /> decrease
                </span>
              )}
              {headerKeys.length > 0 && (
                <button className="om-btn" onClick={() => setAllCollapsed(anyExpanded)}>
                  {anyExpanded ? 'Collapse groups' : 'Expand groups'}
                </button>
              )}
              {matrix.source && <span className="om-source">Started from {matrix.source}</span>}
            </div>

            <div className="om-grid-wrap">
              <table className="om-grid">
                <thead>
                  <tr>
                    <th className="om-col-label">Product</th>
                    {cols.map(c => (
                      <th key={c} className="om-col-color">
                        <span className="om-swatch" style={{ background: SWATCH[c] || '#475569', borderStyle: c === 'No Color' ? 'dashed' : 'solid' }} />
                        {c}
                      </th>
                    ))}
                    <th className="om-col-total">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    let r = -1;
                    return visibleRows.map(row => {
                      if (row.kind === 'header') {
                        const closed = isCollapsed(row.key);
                        return (
                          <tr key={row.key} className="om-row-header"
                            onClick={() => setCollapsed(s => ({ ...s, [row.key]: !closed }))}>
                            <td colSpan={cols.length + 2}>
                              <span className="om-caret">{closed ? '▸' : '▾'}</span> {row.label}
                              {closed && <span className="om-group-sum"> — {groupSums[row.key] > 0 ? `${fmt(groupSums[row.key])} units` : 'click to expand'}</span>}
                            </td>
                          </tr>
                        );
                      }
                      r += 1;
                      const ri = r;
                      let rowTot = 0;
                      return (
                        <tr key={row.key} className={`om-row-${row.kind}`}>
                          <td className="om-col-label" title={row.code}>
                            <span className="om-label">{row.label}</span>
                            <span className="om-code">{row.code}</span>
                          </td>
                          {cols.map((c, ci) => {
                            const sku = row.cells[c];
                            if (!sku) return <td key={c} className="om-cell om-cell-na" />;
                            const v = qty[sku] || 0;
                            rowTot += v;
                            let cmp = '', was = null;
                            if (compareQty) {
                              const p = compareQty[sku] || 0;
                              if (v !== p) { was = p; cmp = p === 0 ? 'cmp-new' : v > p ? 'cmp-up' : 'cmp-down'; }
                            }
                            const isEditing = editing?.sku === sku;
                            const info = layout.skus[sku];
                            return (
                              <td key={c} className={`om-cell ${cmp} ${v ? 'has' : ''}`}
                                title={`${sku}\n${info?.title || ''}${was !== null ? `\nwas ${fmt(was) || 0}` : ''}`}>
                                <input
                                  ref={el => { inputs.current[`${ri}:${ci}`] = el; }}
                                  inputMode="numeric"
                                  value={isEditing ? editing.text : fmt(v)}
                                  onFocus={e => { setEditing({ sku, text: fmt(v) }); e.target.select(); }}
                                  onChange={e => setEditing({ sku, text: e.target.value })}
                                  onBlur={e => { commitCell(sku, e.target.value); setEditing(null); }}
                                  onKeyDown={e => onCellKey(e, ri, ci)}
                                  onPaste={e => onCellPaste(e, ri, ci)}
                                />
                              </td>
                            );
                          })}
                          <td className="om-col-total">{fmt(rowTot)}</td>
                        </tr>
                      );
                    });
                  })()}
                </tbody>
                <tfoot>
                  <tr>
                    <td className="om-col-label">Total</td>
                    {cols.map(c => <td key={c} className="om-col-total">{fmt(totals.cols[c])}</td>)}
                    <td className="om-col-total om-grand">{fmt(totals.grand) || 0}</td>
                  </tr>
                </tfoot>
              </table>
            </div>

            <div className="om-statusbar">
              <span><b>{fmt(totals.grand) || 0}</b> units</span>
              <span><b>{totals.skus}</b> SKUs</span>
              {totals.value > 0 && <span><b>${totals.value.toLocaleString(undefined, { maximumFractionDigits: 0 })}</b> at MSRP</span>}
              {orphanSkus.length > 0 && (
                <span className="om-warn" title={orphanSkus.map(s => `${s}: ${qty[s]}`).join('\n')}>
                  {orphanSkus.length} SKU{orphanSkus.length > 1 ? 's' : ''} with quantities aren't in the current layout (kept, and included in SKU-list exports)
                </span>
              )}
              <span className="om-hint">Enter/↓ moves down · paste a block of numbers into any cell to fill from there</span>
            </div>
          </>
        )}
      </section>

      {pasteOpen && matrix && layout && (
        <PasteDialog initial={pasteInitial} layout={layout} qty={qty}
          onClose={() => setPasteOpen(false)}
          onApply={(next, n) => {
            setQuantities(next, { immediate: true, snapshot: true, note: 'paste' });
            setPasteOpen(false);
            showFlash(`Applied ${n} change${n === 1 ? '' : 's'}`);
          }} />
      )}
      {historyOpen && matrix && (
        <HistoryDialog matrix={matrix} onClose={() => setHistoryOpen(false)}
          onRestore={idx => {
            flushSave().then(() => axios.post(`/api/order-matrix/matrices/${matrix.id}/restore`, { index: idx }))
              .then(r => { setMatrix(r.data); setQty(r.data.quantities || {}); setHistoryOpen(false); loadList(); showFlash('Restored'); })
              .catch(err => setError(errText(err, 'Restore failed')));
          }} />
      )}
    </div>
  );
}

function Menu({ label, children }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const close = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);
  return (
    <div className="om-menu" ref={ref}>
      <button className="om-btn" onClick={() => setOpen(o => !o)}>{label}</button>
      {open && <div className="om-menu-pop" onClick={() => setOpen(false)}>{children}</div>}
    </div>
  );
}

function NewMatrixForm({ list, current, onCancel, onCreated, onError }) {
  const [name, setName] = useState('');
  const [kind, setKind] = useState('order');
  const [from, setFrom] = useState('blank');
  const [copyId, setCopyId] = useState(current?.id || list[0]?.id || '');
  const [locations, setLocations] = useState(null);
  const [location, setLocation] = useState('Shipping Pilot');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (from !== 'stock' || locations) return;
    axios.get('/api/order-matrix/locations')
      .then(r => {
        setLocations(r.data || []);
        if (r.data?.length && !r.data.some(l => l.name === location)) setLocation(r.data[0].name);
      })
      .catch(err => { setLocations([]); onError(errText(err, 'Failed to load locations')); });
  }, [from, locations, location, onError]);

  function submit(e) {
    e.preventDefault();
    setBusy(true);
    const defaultName = from === 'stock' ? `${location} stock ${new Date().toLocaleDateString()}` : 'Untitled matrix';
    axios.post('/api/order-matrix/matrices', {
      name: name.trim() || defaultName, kind: from === 'stock' && kind === 'order' ? 'inventory' : kind,
      from, copy_id: copyId, location,
    }).then(r => onCreated(r.data))
      .catch(err => { onError(errText(err, 'Create failed')); setBusy(false); });
  }

  return (
    <form className="om-new" onSubmit={submit}>
      <input className="om-input" autoFocus placeholder="Name (e.g. MagicCon ATL order)" value={name} onChange={e => setName(e.target.value)} />
      <select className="om-select" value={kind} onChange={e => setKind(e.target.value)}>
        {KINDS.map(k => <option key={k.value} value={k.value}>{k.label}</option>)}
      </select>
      <div className="om-radio">
        <label><input type="radio" checked={from === 'blank'} onChange={() => setFrom('blank')} /> Blank</label>
        <label><input type="radio" checked={from === 'copy'} disabled={!list.length} onChange={() => setFrom('copy')} /> Copy of…</label>
        <label><input type="radio" checked={from === 'stock'} onChange={() => setFrom('stock')} /> Live stock</label>
      </div>
      {from === 'copy' && (
        <select className="om-select" value={copyId} onChange={e => setCopyId(e.target.value)}>
          {list.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
        </select>
      )}
      {from === 'stock' && (
        <select className="om-select" value={location} onChange={e => setLocation(e.target.value)} disabled={!locations}>
          {!locations && <option>Loading locations…</option>}
          {(locations || []).map(l => <option key={l.id} value={l.name}>{l.name}</option>)}
        </select>
      )}
      <div className="om-new-actions">
        <button type="button" className="om-btn" onClick={onCancel}>Cancel</button>
        <button type="submit" className="om-btn om-btn-primary" disabled={busy}>{busy ? 'Creating…' : 'Create'}</button>
      </div>
    </form>
  );
}

function PasteDialog({ initial, layout, qty, onClose, onApply }) {
  const [text, setText] = useState(initial || '');
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const read = useCallback((t) => {
    if (!t.trim()) { setResult(null); return; }
    setBusy(true); setErr(null);
    axios.post('/api/order-matrix/parse', { text: t })
      .then(r => { setResult(r.data); setBusy(false); })
      .catch(e => { setErr(errText(e, 'Could not read paste')); setBusy(false); });
  }, []);

  useEffect(() => { if (initial) read(initial); }, [initial, read]);

  const changes = useMemo(() => {
    if (!result?.quantities) return [];
    return Object.entries(result.quantities)
      .filter(([sku, v]) => (qty[sku] || 0) !== v)
      .map(([sku, v]) => ({ sku, from: qty[sku] || 0, to: v, info: layout.skus[sku] }));
  }, [result, qty, layout]);

  function apply() {
    const next = { ...qty };
    for (const c of changes) { if (c.to) next[c.sku] = c.to; else delete next[c.sku]; }
    onApply(next, changes.length);
  }

  const modeText = {
    grid: 'Read as a matrix (rows matched by product, columns by colour). Blank cells in matched rows become 0.',
    list: 'Read as a SKU list. Only the listed SKUs change.',
    block: 'Read as a bare block of numbers, positionally (same size as the matrix).',
  };

  return (
    <div className="om-modal-bg" onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <div className="om-modal">
        <h3>Paste back</h3>
        <p className="om-muted">Paste the matrix you copied out (header row included), or SKU / qty lines.</p>
        <textarea className="om-paste" autoFocus value={text} placeholder={'Product\tCode\tNo Color\tWhite\tBlue…\nCURV Sleeves Standard 100\tCUR-STA\t\t30\t64…'}
          onChange={e => setText(e.target.value)}
          onPaste={e => { const t = e.clipboardData.getData('text/plain'); setTimeout(() => read(t), 0); }} />
        <div className="om-modal-row">
          <button className="om-btn" onClick={() => read(text)} disabled={busy}>{busy ? 'Reading…' : 'Read paste'}</button>
          {result?.mode && <span className="om-muted">{modeText[result.mode]}</span>}
        </div>
        {err && <div className="om-error"><span>{err}</span></div>}
        {result && (
          <div className="om-preview">
            {result.warnings?.map((w, i) => <div key={i} className="om-warn">{w}</div>)}
            {result.unmatched?.length > 0 && (
              <div className="om-warn">Not matched to any product: {result.unmatched.join(', ')}</div>
            )}
            {result.mode && (
              changes.length ? (
                <table className="om-changes">
                  <thead><tr><th>SKU</th><th>Product</th><th>Colour</th><th>Was</th><th>Now</th></tr></thead>
                  <tbody>
                    {changes.map(c => (
                      <tr key={c.sku}>
                        <td className="mono">{c.sku}</td>
                        <td>{c.info?.title || '—'}</td>
                        <td>{c.info?.color || ''}</td>
                        <td>{fmt(c.from) || 0}</td>
                        <td className={c.to > c.from ? 'up' : 'down'}>{fmt(c.to) || 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : <div className="om-muted">No changes — the paste matches what's already here.</div>
            )}
          </div>
        )}
        <div className="om-modal-actions">
          <button className="om-btn" onClick={onClose}>Cancel</button>
          <button className="om-btn om-btn-primary" disabled={!changes.length} onClick={apply}>
            Apply {changes.length || ''} change{changes.length === 1 ? '' : 's'}
          </button>
        </div>
      </div>
    </div>
  );
}

function HistoryDialog({ matrix, onClose, onRestore }) {
  const hist = (matrix.history || []).map((h, i) => ({ ...h, i })).reverse();
  return (
    <div className="om-modal-bg" onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <div className="om-modal">
        <h3>History — {matrix.name}</h3>
        <p className="om-muted">Earlier versions, saved before each paste, clear or restore, and every ~10 minutes of editing. Restoring keeps the current version in history too.</p>
        {!hist.length && <div className="om-muted">No earlier versions yet.</div>}
        <table className="om-changes">
          <tbody>
            {hist.map(h => {
              const units = Object.values(h.quantities || {}).reduce((a, b) => a + b, 0);
              return (
                <tr key={h.i}>
                  <td>{new Date(h.saved_at * 1000).toLocaleString()}</td>
                  <td>{h.note}</td>
                  <td>{h.by || ''}</td>
                  <td>{fmt(units) || 0} units · {Object.keys(h.quantities || {}).length} SKUs</td>
                  <td><button className="om-btn" onClick={() => onRestore(h.i)}>Restore</button></td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div className="om-modal-actions"><button className="om-btn" onClick={onClose}>Close</button></div>
      </div>
    </div>
  );
}
